import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const slides = [
  {
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1600&h=1000&fit=crop&q=80',
    eyebrow: 'Tax & Advisory Experts',
    heading: 'Expert Advice.',
    headingItalic: 'Real Results.',
    body: 'With over eight years in Accounting and Taxation, we can ensure you the best possible outcome for all your obligations.',
    cta: 'Book an Appointment',
    ctaHref: 'https://www.mytcs.com.au/',
    ctaExternal: true,
    cta2: 'Our Services',
    cta2To: '/services',
  },
  {
    image: 'https://images.unsplash.com/photo-1497215728101-856f4ea42174?w=1600&h=1000&fit=crop&q=80',
    eyebrow: 'Professional Accountants',
    heading: 'Your Growth.',
    headingItalic: 'Our Guidance.',
    body: 'Book a Phone or Zoom Appointment — flexible consultations designed to suit your schedule, wherever you are.',
    cta: 'View Our Services',
    ctaHref: null,
    ctaTo: '/services',
    cta2: 'Contact Us',
    cta2To: '/contact',
  },
  {
    image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1600&h=1000&fit=crop&q=80',
    eyebrow: 'Customer First',
    heading: 'You Succeed.',
    headingItalic: 'We Evolve.',
    body: 'We aim to evolve with our customers by actively engaging with them, anticipating their future needs and supporting their growth.',
    cta: 'Why Choose Us',
    ctaHref: null,
    ctaTo: '/why-choose-us',
    cta2: 'Who We Are',
    cta2To: '/who-we-are',
  },
  {
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1600&h=1000&fit=crop&q=80',
    eyebrow: 'Business Support',
    heading: 'Every Stage.',
    headingItalic: "We're Here.",
    body: 'From startup to growth — we support our clients through every stage of their business journey and life events.',
    cta: 'Get In Touch',
    ctaHref: null,
    ctaTo: '/contact',
    cta2: 'What We Do',
    cta2To: '/services',
  },
];

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback((idx) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(idx);
      setAnimating(false);
    }, 400);
  }, [animating]);

  const next = useCallback(() => goTo((current + 1) % slides.length), [current, goTo]);
  const prev = useCallback(() => goTo((current - 1 + slides.length) % slides.length), [current, goTo]);

  // Auto-advance
  useEffect(() => {
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next]);

  const s = slides[current];

  return (
    <>
      <style>{`
        .hero-wrap { position: relative; padding: 22px 22px 40px; background: var(--off-white); }

        /* ── Slide ── */
        .hero-slide {
          position: relative; min-height: max(640px, 84vh);
          display: flex; align-items: center;
          overflow: hidden; background: var(--charcoal-hero);
          border-radius: var(--radius-lg);
          padding-bottom: 110px;
        }
        .hero-img {
          position: absolute; inset: 0;
          background-size: cover; background-position: center;
          transition: opacity 0.55s ease, transform 6s ease;
          filter: saturate(0.92) brightness(0.96);
        }
        .hero-img.entering { opacity: 0; transform: scale(1.04); }
        .hero-img.visible { opacity: 1; transform: scale(1.0); }
        .hero-img.leaving { opacity: 0; }

        /* Diagonal mask — Carbon Group style */
        .hero-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(
            105deg,
            rgba(42,42,42,0.0) 0%,
            rgba(42,42,42,0.0) 48%,
            rgba(42,42,42,0.88) 58%,
            rgba(42,42,42,0.96) 100%
          );
        }

        .hero-content {
          position: relative; z-index: 2;
          margin-left: auto; width: 50%;
          padding: 80px 0 80px 60px;
          animation: slideIn 0.6s ease both;
        }
        @keyframes slideIn {
          from { opacity:0; transform: translateX(28px); }
          to { opacity:1; transform: translateX(0); }
        }
        .hero-eyebrow {
          display: inline-flex; align-items: center; gap: 10px;
          font-size: 11px; font-weight: 600; letter-spacing: 3.5px;
          text-transform: uppercase; color: var(--gold);
          margin-bottom: 18px;
        }
        .hero-eyebrow::before {
          content: ''; display: block;
          width: 28px; height: 2px; background: var(--gold);
        }
        .hero-h1 {
          font-family: var(--font-display);
          font-size: clamp(38px, 4.5vw, 62px);
          font-weight: 700; color: white;
          line-height: 1.1; margin-bottom: 4px;
        }
        .hero-h1-italic {
          font-family: var(--font-display);
          font-size: clamp(38px, 4.5vw, 62px);
          font-weight: 400; font-style: italic;
          color: var(--gold); line-height: 1.15;
          margin-bottom: 24px; display: block;
        }
        .hero-body {
          font-size: 17px; font-weight: 300; line-height: 1.75;
          color: rgba(255,255,255,0.78); max-width: 460px;
          margin-bottom: 36px;
        }
        .hero-btns { display: flex; gap: 14px; flex-wrap: wrap; }

        /* Stats bar */
        .hero-stats {
          position: absolute; bottom: 0; left: 0; right: 0; z-index: 3;
          background: rgba(26,26,24,0.75); backdrop-filter: blur(10px);
          border-top: 1px solid rgba(201,168,76,0.2);
          display: flex;
        }
        .hero-stat {
          flex: 1; padding: 18px 0; text-align: center;
          border-right: 1px solid rgba(201,168,76,0.15);
        }
        .hero-stat:last-child { border-right: none; }
        .hs-num {
          font-family: var(--font-display);
          font-size: 28px; font-weight: 700; color: var(--gold);
          line-height: 1;
        }
        .hs-label {
          font-size: 11px; font-weight: 500; letter-spacing: 1.5px;
          text-transform: uppercase; color: rgba(255,255,255,0.45);
          margin-top: 4px;
        }

        /* Nav arrows */
        .slide-arrow {
          position: absolute; top: 50%; transform: translateY(-50%);
          z-index: 4; width: 48px; height: 48px; border-radius: 50%;
          background: rgba(255,255,255,0.12); backdrop-filter: blur(8px);
          border: 1px solid rgba(255,255,255,0.2);
          display: flex; align-items: center; justify-content: center;
          color: white; cursor: pointer; transition: all 0.25s ease;
        }
        .slide-arrow:hover { background: var(--gold); color: var(--charcoal-dark); border-color: var(--gold); transform: translateY(-50%) scale(1.06); }
        .slide-arrow-prev { left: 24px; }
        .slide-arrow-next { right: 24px; }

        @media (max-width: 900px) {
          .hero-wrap { padding: 14px 14px 32px; }
          .hero-slide { min-height: 70vh; align-items: flex-end; padding-bottom: 100px; border-radius: var(--radius); }
          .hero-overlay {
            background: linear-gradient(180deg, rgba(42,42,42,0.2) 0%, rgba(42,42,42,0.9) 60%, rgba(42,42,42,0.97) 100%);
          }
          .hero-content { width: 100%; padding: 0 0 24px 0; }
          .hero-stats { display: none; }
          .slide-arrow { display: none; }
        }
        @media (max-width: 600px) {
          .hero-body { font-size: 15px; }
          .hero-h1 { font-size: 32px; }
          .hero-h1-italic { font-size: 32px; }
        }
      `}</style>

      <div className="hero-wrap">
        <div className="hero-slide">
          {/* Background image */}
          <div
            className={`hero-img ${animating ? 'leaving' : 'visible'}`}
            style={{ backgroundImage: `url(${s.image})` }}
          />
          <div className="hero-overlay" />

          {/* Content */}
          <div className="container" style={{position:'relative',zIndex:2,width:'100%'}}>
            <div className="hero-content" key={current}>
              <div className="hero-eyebrow">{s.eyebrow}</div>
              <h1 className="hero-h1">{s.heading}</h1>
              <span className="hero-h1-italic">{s.headingItalic}</span>
              <p className="hero-body">{s.body}</p>
              <div className="hero-btns">
                {s.ctaExternal ? (
                  <a href={s.ctaHref} target="_blank" rel="noreferrer" className="btn-primary">
                    {s.cta} <ArrowRight size={15} />
                  </a>
                ) : (
                  <Link to={s.ctaTo || '/contact'} className="btn-primary">
                    {s.cta} <ArrowRight size={15} />
                  </Link>
                )}
                <Link to={s.cta2To} className="btn-secondary">
                  {s.cta2}
                </Link>
              </div>
            </div>
          </div>

          {/* Arrows */}
          <button className="slide-arrow slide-arrow-prev" onClick={prev}><ChevronLeft size={20} /></button>
          <button className="slide-arrow slide-arrow-next" onClick={next}><ChevronRight size={20} /></button>

          {/* Stats */}
          <div className="hero-stats">
            {[['8+', "Years' Experience"],['500+','Clients Served'],['100%','Client Focused'],['0','Hidden Fees']].map(([n,l]) => (
              <div key={l} className="hero-stat">
                <div className="hs-num">{n}</div>
                <div className="hs-label">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
